import { CheckCircle, XCircle, Download, Trash2, Clock } from "lucide-react";
import Table from "../common/table/Table";
import { useEffect, useState } from "react";
import {
  deleteDockerImages,
  getDockerImages,
} from "../../API/Company-admin/userApi";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import { CircularProgress } from "@mui/material";
import JSZip from "jszip";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";

// // Sample data - in a real application, this would come from an API
// const dockerImages = [
//   {
//     _id: "680b1d458e921848bb1df535",
//     projectId: "64ba2a43119fadbd19bef5e9",
//     projectName: "LogicProject",
//     buildId: "m9wco7byrlaxcozfb8d",
//     imageName: "test01",
//     status: "success",
//     createdAt: "2025-04-25T05:27:33.418Z",
//     performedBy: "bindu.a@redsling.com",
//     dockerLink: "https://127.0.0.1:8085/static-tars/64ba2a43119fadbd19bef5e9/test01.tar",
//   },
//   {
//     _id: "680b1d458e921848bb1df536",
//     projectId: "64ba2a43119fadbd19bef5e9",
//     projectName: "LogicProject",
//     buildId: "k8xco7byrlaxcozfb9e",
//     imageName: "test02",
//     status: "failed",
//     createdAt: "2025-04-24T14:15:22.118Z",
//     performedBy: "bindu.a@redsling.com",
//     dockerLink: "https://127.0.0.1:8085/static-tars/64ba2a43119fadbd19bef5e9/test02.tar",
//   },
//   {
//     _id: "680b1d458e921848bb1df537",
//     projectId: "64ba2a43119fadbd19bef5e9",
//     projectName: "LogicProject",
//     buildId: "p7zco7byrlaxcozfb7f",
//     imageName: "test03",
//     status: "success",
//     createdAt: "2025-04-23T09:42:11.332Z",
//     performedBy: "bindu.a@redsling.com",
//     dockerLink: "https://127.0.0.1:8085/static-tars/64ba2a43119fadbd19bef5e9/test03.tar",
//   },
// ]

export default function DockerImageList(props) {
  const { userInfo } = props;
  const [dockerImages, setDockerImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedProjectName, setSelectedProjectName] = useState("");
  const [selectedProjectId, setSelectedProjectid] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleConfirmClose = async (confirmed) => {
    setConfirmOpen(false);
    if (confirmed) {
      try {
        setIsDeleting(true);
        await deleteDockerImages(selectedProjectId, userInfo.token);
        getDockerDetails();
      } catch (error) {
        console.error("Error deleting docker image:", error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  //   const mockApiDelete = async (id) => {
  //     return new Promise((resolve, reject) => {
  //       setTimeout(() => {
  //         if (!id) {
  //           reject(new Error('ID is required'));
  //         } else if (parseInt(id, 10) % 2 === 0) {
  //           resolve(true);
  //         } else {
  //           reject(new Error('Can not delete odd numbered elements'));
  //         }
  //       }, 1000);
  //     });
  // };

  const getDockerDetails = async () => {
    setLoading(true);
    try {
      const response = await getDockerImages(
        userInfo?.organisation_id,
        userInfo.token
      );
      setDockerImages(response);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching docker images:", error);
    }
    // Fetch the docker details from the API and set it to state
    // This is a placeholder function. You would replace this with your actual API call.
  };

  useEffect(() => {
    getDockerDetails();
  }, []);

  // Helper: Convert base64 string to Blob
  function b64toBlob(b64Data, contentType = "", sliceSize = 512) {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length)
        .fill()
        .map((_, i) => slice.charCodeAt(i));
      byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, { type: contentType });
  }

  const downloadAllAsZip = async (response, docker_image_name) => {
    const zip = new JSZip();
    const folder = zip.folder("BuildPackage");

    // Add collections.json
    if (response.collections) {
      folder.file(
        `${docker_image_name} collections.json`,
        JSON.stringify(response.collections, null, 2)
      );
    }

    // Add archiveBase64 as archive.zip
    if (response.archiveBase64) {
      const archiveBlob = b64toBlob(response.archiveBase64, "application/zip");
      folder.file("archive.zip", archiveBlob);
    }

    // Add Docker TAR file from URL
    // if (response.url) {
    //   try {
    //     const dockerRes = await fetch(response.url);
    //     const dockerBlob = await dockerRes.blob();
    //     folder.file(`${docker_image_name}.tar`, dockerBlob);
    //   } catch (err) {
    //     console.error("Error fetching Docker file:", err);
    //   }
    // }

    if (response.url) {
      try {
        // Create a link element and trigger download via <a> tag
        const link = document.createElement("a");
        link.href = response.url; // Directly set the URL for download
        link.download = `${docker_image_name}.tar`; // Set the name of the TAR file
        document.body.appendChild(link); // Append the <a> tag to the body
        link.click(); // Trigger the download
        document.body.removeChild(link); // Clean up by removing the <a> tag
      } catch (err) {
        console.error("Error creating download link for Docker file:", err);
      }
    }

    // Generate ZIP and trigger download
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, "Collection.zip");
    toast.success("Downloading Docker Files...");
  };

  const columns = [
    {
      header: "Sl No",
      accessor: "_id",
      cell: (value, row, index) => {
        return index + 1;
      },
    },
    {
      header: "Project Name",
      accessor: "projectName",
    },
    {
      header: "Build Id",
      accessor: "buildId",
    },
    {
      header: "Image Name",
      accessor: "imageName",
    },
    {
      header: "Status",
      accessor: "status",
      cell: (value) =>
        value === "success" ? (
          <div className="bg-green-50 text-green-700 border-green-200 flex items-center gap-1 px-2 py-1 rounded-md">
            <CheckCircle className="h-3.5 w-3.5" />
            <span>Success</span>
          </div>
        ) : value === "pending" ? (
          <div className="bg-yellow-50 text-yellow-700 border-yellow-200 flex items-center gap-1 px-2 py-1 rounded-md">
            <Clock className="h-3.5 w-3.5" />
            <span>Pending</span>
          </div>
        ) : (
          <div className="bg-red-50 text-red-700 border-red-200 flex items-center gap-1 px-2 py-1 rounded-md">
            <XCircle className="h-3.5 w-3.5" />
            <span>Failed</span>
          </div>
        ),
    },
    {
      header: "Generated By",
      accessor: "performedBy",
    },
    {
      header: "Generated Time",
      accessor: "createdAt",
      cell: (value) => <p>{formatDate(value)}</p>,
    },
    {
      header: "Actions",
      accessor: "actions",
      cell: (value, row) => {
        const handleDownload = async () => {
          const response = {
            collections: row.jsonCollection,
            archiveBase64: row.archive,
            url: row.dockerLink,
          };
          const docker_image_name = row?.imageName || "docker_image";
          await downloadAllAsZip(response, docker_image_name);
        };
        return (
          <div className="py-2 flex justify-end gap-4">
            <button onClick={handleDownload} className="h-4">
              <Download className="h-4 w-4 mr-1" />
            </button>
            <button
              className="h-4"
              onClick={() => {
                setSelectedProjectName(row?.projectName || "");
                setSelectedProjectid(row?._id);
                setConfirmOpen(true);
              }}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <CircularProgress size={16} />
              ) : (
                <Trash2 className="h-4 w-4 mr-1" />
              )}
            </button>
          </div>
        );
      },
    },
  ];

  const pageSize = 10;
  const totalItems = dockerImages?.length;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  // Format date function
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="container mx-auto py-6 px-4">
      <Table
        columns={columns}
        data={dockerImages}
        loading={loading}
        pagination={{
          currentPage: currentPage,
          pageSize,
          totalItems: totalItems,
          onPageChange: handlePageChange,
        }}
        className="bg-white rounded-lg shadow"
      />

      <ConfirmDialog
        open={confirmOpen}
        projectName={selectedProjectName}
        title="Confirm Delete"
        content={`Are you sure you want to delete item?`}
        onClose={handleConfirmClose}
      />
    </div>
  );
}
